/**
 * "Pre-Kickoff Data" AI-enrichment panel (TIER-2 fidelity parity).
 *
 * 1:1 port of the legacy SPA `../EER app/public/assets/js/project.js`:
 *   - `renderPreKickoffPanel` (~1591-1611): a collapsible `<details>` with two
 *     clickable one-line previews — "AI Analysis" (blue) and "Context" (grey) —
 *     each truncated to 120 chars;
 *   - `openPreKickoffDetail` (~1664-1694): clicking a preview opens a detail dialog
 *     showing the FULL data — for AI Analysis: Summary, Recommendation,
 *     Classification, Full text and Key points; for Context: the manual context
 *     text verbatim.
 *
 * Thin shell — the non-render rules live in `./prekickoffModel.ts`
 * (analysisPreviewText / buildAnalysisDetail / truncate) and the detail chrome is
 * the shared `ui/Dialog.tsx`. The panel is hidden entirely when neither an AI
 * analysis nor a manual context is present (legacy `if (!analysisText &&
 * !manualContext) return ''`).
 */
import { useState } from 'react';
import {
  analysisPreviewText,
  buildAnalysisDetail,
  truncate,
  type AnalysisDetailSection,
} from './prekickoffModel.js';
import type { PreKickoffAnalysis } from '@eer/shared';
import { Dialog } from '../ui/Dialog.js';
import { RichTextOrPlain } from '../ui/RichTextOrPlain.js';
import { richTextToPlainText } from '@eer/shared';

/** Which detail dialog is open (or null when closed). */
type OpenDetail = 'analysis' | 'context' | null;

export interface PreKickoffDataPanelProps {
  /** The persisted `analysis` artifact content (null when never run). */
  readonly analysis?: PreKickoffAnalysis | null;
  /** The project's manual context text (`data.manual_context.text`), '' when none. */
  readonly manualContext?: string;
  /** Whether the `<details>` starts expanded. Default false (legacy `open===true`). */
  readonly defaultOpen?: boolean;
}

export function PreKickoffDataPanel({
  analysis,
  manualContext = '',
  defaultOpen = false,
}: PreKickoffDataPanelProps): React.JSX.Element | null {
  const [open, setOpen] = useState<OpenDetail>(null);

  const analysisText = analysis ? analysisPreviewText(analysis) : '';
  const context = manualContext.trim();

  // Legacy: render nothing when there is neither an analysis nor a context.
  if (!analysisText && !context) return null;

  const detailSections: readonly AnalysisDetailSection[] =
    open === 'analysis' && analysis ? buildAnalysisDetail(analysis) : [];

  return (
    <details
      data-testid="prekickoff-data-panel"
      className="rounded-lg border border-border p-4"
      open={defaultOpen}
    >
      <summary className="cursor-pointer select-none text-sm font-bold text-text-strong">
        <i className="fa-solid fa-brain mr-1 text-blue-500" aria-hidden />
        Pre-Kickoff Data
      </summary>

      <div className="mt-2 space-y-2">
        {analysisText ? (
          <button
            type="button"
            data-testid="prekickoff-data-analysis"
            onClick={() => setOpen('analysis')}
            className="w-full rounded border-l-4 border-blue-300 bg-blue-50 p-3 text-left text-xs text-slate-700 transition-opacity hover:opacity-80"
          >
            <strong>AI Analysis:</strong> {truncate(richTextToPlainText(analysisText), 120)}
          </button>
        ) : null}

        {context ? (
          <button
            type="button"
            data-testid="prekickoff-data-context"
            onClick={() => setOpen('context')}
            className="w-full rounded border-l-4 border-gray-300 bg-gray-50 p-3 text-left text-xs text-slate-700 transition-opacity hover:opacity-80"
          >
            <strong>Context:</strong> {truncate(richTextToPlainText(context), 120)}
          </button>
        ) : null}
      </div>

      {/* AI Analysis detail dialog */}
      <Dialog
        open={open === 'analysis'}
        onClose={() => setOpen(null)}
        title="AI Analysis"
        testId="prekickoff-data-analysis-dialog"
        maxWidth="2xl"
      >
        {detailSections.length === 0 ? (
          <p data-testid="prekickoff-data-analysis-empty" className="text-sm text-text-muted">
            No analysis data
          </p>
        ) : (
          <div className="space-y-3">
            {detailSections.map((section) => (
              <div
                key={section.label}
                data-testid={`prekickoff-data-section-${slug(section.label)}`}
              >
                <strong className="text-xs uppercase tracking-wide text-text-muted">
                  {section.label}
                </strong>
                {section.items ? (
                  <ul className="mt-1 list-inside list-disc space-y-0.5 text-sm text-slate-700">
                    {section.items.map((item, i) => (
                      <li key={i}>
                        <RichTextOrPlain value={item} className="inline" />
                      </li>
                    ))}
                  </ul>
                ) : (
                  <RichTextOrPlain value={section.value} className="mt-1 text-sm text-slate-700" />
                )}
              </div>
            ))}
          </div>
        )}
      </Dialog>

      {/* Manual Context detail dialog */}
      <Dialog
        open={open === 'context'}
        onClose={() => setOpen(null)}
        title="Manual Context"
        testId="prekickoff-data-context-dialog"
        maxWidth="2xl"
      >
        {context ? (
          <RichTextOrPlain
            value={context}
            testId="prekickoff-data-context-text"
            className="text-sm leading-relaxed text-slate-700"
          />
        ) : (
          <p className="text-sm text-text-muted">No context</p>
        )}
      </Dialog>
    </details>
  );
}

/** Lowercase-dash a section label for a stable testid (Key points → key-points). */
function slug(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export default PreKickoffDataPanel;
